import React, { useState, useEffect } from 'react';
import TagManagerModal from './TagManagerModal';
import { useTranslation } from 'react-i18next';
import { 
  Upload, Search, Grid, List, Folder, Tag, 
  Image, Video, FileText, Globe, Play, 
  Edit2, Trash2, Eye, X, Filter, Download,
  FolderPlus, Plus, ChevronRight, ChevronDown,
  Check, MoreVertical
} from 'lucide-react';

const EnhancedMediaLibrary = ({ currentUser }) => {
  const { t } = useTranslation();
  const [media, setMedia] = useState([]);
  const [folders, setFolders] = useState([]);
  const [tags, setTags] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [selectedTags, setSelectedTags] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [selectedMedia, setSelectedMedia] = useState([]);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(null);
  const [showFolderModal, setShowFolderModal] = useState(false);
  const [showTagModal, setShowTagModal] = useState(false);
  const [filterType, setFilterType] = useState('all');
  const [expandedFolders, setExpandedFolders] = useState([]);
  const [uploadMethod, setUploadMethod] = useState('file');
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [editingTag, setEditingTag] = useState(null);
  const [newTagName, setNewTagName] = useState('');
  const [newTagColor, setNewTagColor] = useState('#2563eb');
  const [showMediaTagModal, setShowMediaTagModal] = useState(null);
  const [newMedia, setNewMedia] = useState({ 
    name: '', 
    type: 'image', 
    url: '', 
    duration: 10 
  });

  const API_URL = (process.env.REACT_APP_API_URL?.replace('/api', '') || 'https://app.alpenscreen.ch');
  const API_BASE_URL = (process.env.REACT_APP_API_URL?.replace('/api', '') || 'https://app.alpenscreen.ch');
  const token = localStorage.getItem('token');

  // Fetch Data
  useEffect(() => {
    fetchMedia();
    fetchFolders();
    fetchTags();
  }, []);

  const fetchMedia = async () => {
    try {
      const res = await fetch(`${API_URL}/api/media`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setMedia(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching media:', err);
      setMedia([]);
    }
  };

  const fetchFolders = async () => {
    try {
      const res = await fetch(`${API_URL}/api/folders`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setFolders(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching folders:', err);
      setFolders([]);
    }
  };

  const fetchTags = async () => {
    try {
      const res = await fetch(`${API_URL}/api/tags`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setTags(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching tags:', err);
      setTags([]);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      setNewMedia({ ...newMedia, name: file.name });
    }
  };

  // Create Tag
  const handleCreateTag = async (name, color) => {
    try {
      const res = await fetch(`${API_URL}/api/tags`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ name, color })
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || t('media.failedToCreateTag'));
      }

      fetchTags();
      setShowTagModal(false);
    } catch (error) {
      alert(t('common.error') + ': ' + error.message);
    }
  };

  // Delete Tag
  const deleteTag = async (tagId) => {
    if (!window.confirm(t('media.deleteTagConfirm'))) return;

    try {
      await fetch(`${API_URL}/api/tags/${tagId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchTags();
    } catch (err) {
      alert(t('media.errorDeletingTag'));
    }
  };

  // Assign Tag to Media
  const assignTagToMedia = async (mediaId, tagId) => {
    try {
      await fetch(`${API_URL}/api/media/${mediaId}/tags/${tagId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const mediaRes = await fetch(`${API_URL}/api/media`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const allMedia = await mediaRes.json();
      const freshMedia = allMedia.find(m => m.id === mediaId);
      
      setMedia(allMedia);
      
      if (freshMedia) {
        setShowMediaTagModal(freshMedia);
      }
    } catch (err) {
      console.error('Error assigning tag:', err);
      alert(t('media.errorAssigningTag'));
    }
  };

  // Remove Tag from Media
  const removeTagFromMedia = async (mediaId, tagId) => {
    try {
      await fetch(`${API_URL}/api/media/${mediaId}/tags/${tagId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const mediaRes = await fetch(`${API_URL}/api/media`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const allMedia = await mediaRes.json();
      const freshMedia = allMedia.find(m => m.id === mediaId);
      
      setMedia(allMedia);
      
      if (freshMedia) {
        setShowMediaTagModal(freshMedia);
      }
    } catch (err) {
      console.error('Error removing tag:', err);
      alert(t('media.errorRemovingTag'));
    }
  };

  const uploadMedia = async () => {
    if (uploadMethod === 'file' && !selectedFile) {
      alert(t('media.selectFile'));
      return;
    }

    if (uploadMethod === 'url') {
      const urlInput = document.getElementById('urlInput').value;
      const urlMediaName = document.getElementById('urlMediaName').value;
      const urlDuration = document.getElementById('urlDuration').value;

      if (!urlInput || !urlMediaName) {
        alert(t('media.enterNameAndUrl'));
        return;
      }

      if (!urlInput.startsWith('http://') && !urlInput.startsWith('https://')) {
        alert(t('media.urlMustStartWith'));
        return;
      }

      setUploading(true);

      try {
        const response = await fetch(`${API_URL}/api/media/url`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            name: urlMediaName,
            url: urlInput,
            type: 'webpage',
            duration: parseInt(urlDuration) || 30,
            folder_id: selectedFolder
          })
        });

        if (!response.ok) throw new Error(t('media.failedToAddUrl'));

        await Promise.all([fetchMedia(), fetchFolders()]);
        setShowUploadModal(false);
        setUploadMethod('file');
      } catch (error) {
        alert(t('media.uploadError') + ' ' + error.message);
      } finally {
        setUploading(false);
      }
      return;
    }

    // File Upload
    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('duration', '10');
      if (selectedFolder) {
        formData.append('folder_id', selectedFolder.toString());
      }

      const response = await fetch(`${API_URL}/api/media/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      if (!response.ok) throw new Error(t('media.uploadFailed'));

      await Promise.all([fetchMedia(), fetchFolders()]);
      setShowUploadModal(false);
      setSelectedFile(null);
    } catch (error) {
      alert(t('media.uploadError') + ' ' + error.message);
    } finally {
      setUploading(false);
    }
  };

  // Delete Media
  const deleteMedia = async (id) => {
    if (!window.confirm(t('media.deleteConfirm'))) return;

    try {
      await fetch(`${API_URL}/api/media/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      await Promise.all([
        fetchMedia(),
        fetchFolders()
      ]);
    } catch (err) {
      alert(t('media.errorDeletingMedia'));
    }
  };

  // Bulk Delete
  const bulkDeleteMedia = async () => {
    if (!window.confirm(t('media.bulkDeleteConfirm', { count: selectedMedia.length }))) return;

    try {
      await fetch(`${API_URL}/api/media/bulk-delete`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ mediaIds: selectedMedia })
      });
      setSelectedMedia([]);
      
      await Promise.all([
        fetchMedia(),
        fetchFolders()
      ]);
    } catch (err) {
      alert(t('media.errorDeletingMedia'));
    }
  };

  // Toggle Media Selection
  const toggleMediaSelection = (id) => {
    setSelectedMedia(prev =>
      prev.includes(id)
        ? prev.filter(mediaId => mediaId !== id)
        : [...prev, id]
    );
  };

  // Select All
  const selectAll = () => {
    if (selectedMedia.length === filteredMedia.length) {
      setSelectedMedia([]);
    } else {
      setSelectedMedia(filteredMedia.map(m => m.id));
    }
  };

  // Filter Media
  const filteredMedia = media.filter(item => {
    const matchesSearch = item.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesFolder = !selectedFolder || item.folder_id === selectedFolder;
    const matchesTags = selectedTags.length === 0 || 
      (item.tags && selectedTags.every(tag => item.tags.includes(tag)));
    const matchesType = filterType === 'all' || item.type === filterType;
    
    return matchesSearch && matchesFolder && matchesTags && matchesType;
  });

  // Get Media Icon
  const getMediaIcon = (type) => {
    switch(type) {
      case 'image': return <Image size={20} />;
      case 'video': return <Video size={20} />;
      case 'pdf': return <FileText size={20} />;
      case 'webpage': return <Globe size={20} />;
      default: return <FileText size={20} />;
    }
  };

  // Get Type Badge Color
  const getTypeBadgeColor = (type) => {
    switch(type) {
      case 'image': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'video': return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case 'pdf': return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      case 'webpage': return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
    }
  };

  // Toggle Folder Expansion
  const toggleFolder = (folderId) => {
    setExpandedFolders(prev =>
      prev.includes(folderId)
        ? prev.filter(id => id !== folderId)
        : [...prev, folderId]
    );
  };

  // Build Folder Tree
  const buildFolderTree = (parentId = null) => {
    return folders
      .filter(f => f.parent_id === parentId)
      .map(folder => ({
        ...folder,
        children: buildFolderTree(folder.id)
      }));
  };

  const folderTree = buildFolderTree();

  // Render Folder Tree Item
  const FolderTreeItem = ({ folder, level = 0, onDeleteFolder }) => {
    const isExpanded = expandedFolders.includes(folder.id);
    const isSelected = selectedFolder === folder.id;
    const hasChildren = folder.children && folder.children.length > 0;

    return (
      <div>
        <div
          className={`group flex items-center gap-2 px-3 py-2 rounded cursor-pointer transition-colors ${
            isSelected
              ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300'
              : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'
          }`}
          style={{ paddingLeft: `${level * 20 + 12}px` }}
          onClick={() => setSelectedFolder(folder.id)}
        >
          {hasChildren && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                toggleFolder(folder.id);
              }}
              className="flex-shrink-0"
            >
              {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            </button>
          )}
          {!hasChildren && <div className="w-4" />}
          <Folder size={16} className="flex-shrink-0" />
          <span className="text-sm font-medium truncate flex-1">{folder.name}</span>
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {folder.media_count || 0}
          </span>
          
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDeleteFolder(folder.id);
            }}
            className="opacity-0 group-hover:opacity-100 p-1 hover:bg-red-100 dark:hover:bg-red-900 rounded text-red-600 dark:text-red-400 transition-opacity flex-shrink-0"
            title={t('media.deleteFolder')}
          >
            <Trash2 size={14} />
          </button>
        </div>
        {hasChildren && isExpanded && (
          <div>
            {folder.children.map(child => (
              <FolderTreeItem key={child.id} folder={child} level={level + 1} onDeleteFolder={onDeleteFolder} />
            ))}
          </div>
        )}
      </div>
    );
  };

  // Stats
  const stats = {
    total: media.length,
    images: media.filter(m => m.type === 'image').length,
    videos: media.filter(m => m.type === 'video').length,
    others: media.filter(m => !['image', 'video'].includes(m.type)).length
  };

  // Media Tag Assignment Modal
  const MediaTagModal = ({ media }) => {
    const mediaTags = media.tag_ids || [];

    return (
      <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg max-w-md w-full p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-bold text-gray-900 dark:text-white">
              {t('media.manageTags')} {media.name}
            </h3>
            <button
              onClick={() => setShowMediaTagModal(null)}
              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              <X size={24} />
            </button>
          </div>

          <div className="space-y-2 max-h-96 overflow-y-auto">
            {tags.length === 0 ? (
              <p className="text-gray-500 dark:text-gray-400 text-center py-4">
                {t('media.noTagsAvailable')}
              </p>
            ) : (
              tags.map(tag => {
                const isAssigned = mediaTags.includes(tag.id);
                return (
                  <div
                    key={tag.id}
                    className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className="w-4 h-4 rounded"
                        style={{ backgroundColor: tag.color }}
                      ></div>
                      <span className="font-medium text-gray-900 dark:text-white">{tag.name}</span>
                    </div>
                    <button
                      onClick={() => {
                        if (isAssigned) {
                          removeTagFromMedia(media.id, tag.id);
                        } else {
                          assignTagToMedia(media.id, tag.id);
                        }
                      }}
                      className={`px-3 py-1 rounded-lg text-sm transition-colors ${
                        isAssigned
                          ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'
                          : 'bg-gray-200 text-gray-700 dark:bg-gray-600 dark:text-gray-300'
                      }`}
                    >
                      {isAssigned ? t('media.assigned') : t('media.assign')}
                    </button>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    );
  };

  // Delete Folder
  const deleteFolder = async (folderId) => {
    if (!window.confirm(t('media.deleteFolderConfirm'))) return;

    try {
      const res = await fetch(`${API_URL}/api/folders/${folderId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!res.ok) {
        const error = await res.json();
        alert(error.error || t('media.failedToDeleteFolder'));
        return;
      }

      fetchFolders();
      if (selectedFolder === folderId) {
        setSelectedFolder(null);
      }
    } catch (error) {
      alert(t('common.error') + ': ' + error.message);
    }
  };

  // Folder Modal Component
  const FolderModal = () => {
    const [folderName, setFolderName] = useState('');
    const [parentId, setParentId] = useState(null);

    const createFolder = async () => {
      if (!folderName.trim()) {
        alert(t('media.enterFolderName'));
        return;
      }

      try {
        const res = await fetch(`${API_URL}/api/folders`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            name: folderName,
            parent_id: parentId
          })
        });

        if (!res.ok) {
          const error = await res.json();
          throw new Error(error.error || t('media.failedToCreateFolder'));
        }

        setShowFolderModal(false);
        setFolderName('');
        setParentId(null);
        fetchFolders();
      } catch (error) {
        alert(t('common.error') + ': ' + error.message);
      }
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg max-w-md w-full p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-bold text-gray-900 dark:text-white">
              {t('media.createNewFolder')}
            </h3>
            <button
              onClick={() => setShowFolderModal(false)}
              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              <X size={24} />
            </button>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                {t('media.folderName')} *
              </label>
              <input
                type="text"
                value={folderName}
                onChange={(e) => setFolderName(e.target.value)}
                placeholder={t('media.folderNamePlaceholder')}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                autoFocus
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                {t('media.parentFolder')}
              </label>
              <select
                value={parentId || ''}
                onChange={(e) => setParentId(e.target.value ? parseInt(e.target.value) : null)}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              >
                <option value="">{t('media.rootNoParent')}</option>
                {folders.map(folder => (
                  <option key={folder.id} value={folder.id}>
                    {folder.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex gap-3 mt-6">
            <button
              onClick={createFolder}
              className="flex-1 bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg transition-colors"
            >
              {t('media.createFolder')}
            </button>
            <button
              onClick={() => {
                setShowFolderModal(false);
                setFolderName('');
                setParentId(null);
              }}
              className="flex-1 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200 px-4 py-2 rounded-lg transition-colors"
            >
              {t('common.cancel')}
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="flex h-[calc(100vh-200px)] gap-4">
      {/* Left Sidebar - Folders */}
      <div className="hidden lg:block w-64 bg-white dark:bg-gray-800 rounded-lg shadow p-4 overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-gray-900 dark:text-white">{t('media.folders')}</h3>
          <button
            onClick={() => setShowFolderModal(true)}
            className="text-primary-600 hover:text-blue-700 dark:text-primary-400"
            title={t('media.newFolder')}
          >
            <FolderPlus size={18} />
          </button>
        </div>

        <button
          onClick={() => setSelectedFolder(null)}
          className={`w-full text-left px-3 py-2 rounded mb-2 transition-colors ${
            !selectedFolder
              ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300'
              : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'
          }`}
        >
          <div className="flex items-center gap-2">
            <Folder size={16} />
            <span className="text-sm font-medium">{t('media.allMedia')}</span>
            <span className="ml-auto text-xs text-gray-500 dark:text-gray-400">
              {media.length}
            </span>
          </div>
        </button>

        <div className="space-y-1">
          {folderTree.map(folder => (
            <FolderTreeItem key={folder.id} folder={folder} onDeleteFolder={deleteFolder} />
          ))}
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col gap-4 overflow-hidden">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-3 md:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">{t('media.totalMedia')}</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.total}</p>
              </div>
              <Grid className="text-primary-600" size={32} />
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-3 md:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">{t('media.images')}</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.images}</p>
              </div>
              <Image className="text-green-500" size={32} />
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-3 md:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">{t('media.videos')}</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.videos}</p>
              </div>
              <Video className="text-primary-600" size={32} />
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-3 md:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">{t('media.others')}</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.others}</p>
              </div>
              <FileText className="text-purple-500" size={32} />
            </div>
          </div>
        </div>

        {/* Toolbar */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-3 md:p-4">
          <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
            {/* Search */}
            <div className="relative flex-1 w-full lg:max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder={t('media.searchMedia')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              />
            </div>

            {/* Filters & Actions */}
            <div className="flex gap-2 flex-wrap">
              {/* Type Filter */}
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
              >
                <option value="all">{t('media.allTypes')}</option>
                <option value="image">{t('media.images')}</option>
                <option value="video">{t('media.videos')}</option>
                <option value="pdf">PDFs</option>
                <option value="webpage">{t('media.webpages')}</option>
              </select>

              {/* View Mode Toggle */}
              <div className="flex border border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 ${viewMode === 'grid' ? 'bg-primary-600 text-white' : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300'}`}
                >
                  <Grid size={20} />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 ${viewMode === 'list' ? 'bg-primary-600 text-white' : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300'}`}
                >
                  <List size={20} />
                </button>
              </div>

              {/* Select All */}
              <button
                onClick={selectAll}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                title={t('media.selectAll')}
              >
                <Check size={20} />
              </button>

              {/* Tag Manager Button */}
              <button
                onClick={() => setShowTagModal(true)}
                className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg transition-colors"
                title={t('media.manageTags')}
              >
                <Tag size={20} />
                {t('media.tags')}
              </button>

              {/* Upload Button */}
              <button
  onClick={() => setShowUploadModal(true)}
  className="flex items-center gap-2 bg-primary-600 hover:bg-primary-700 text-white px-4 py-3 md:py-2 rounded-lg transition-colors font-semibold"
>
                <Upload size={20} />
                {t('media.upload')}
              </button>
            </div>
          </div>
        </div>

        {/* Bulk Actions Bar */}
        {selectedMedia.length > 0 && (
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-blue-900 dark:text-blue-100">
                {t('media.itemsSelected', { count: selectedMedia.length })}
              </span>
              <div className="flex gap-2">
                <button
                  onClick={bulkDeleteMedia}
                  className="flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-3 py-1.5 rounded-lg text-sm transition-colors"
                >
                  <Trash2 size={16} />
                  {t('common.delete')}
                </button>
                <button
                  onClick={() => setSelectedMedia([])}
                  className="flex items-center gap-2 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200 px-3 py-1.5 rounded-lg text-sm transition-colors"
                >
                  <X size={16} />
                  {t('common.cancel')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Media Grid/List */}
        <div className="flex-1 bg-white dark:bg-gray-800 rounded-lg shadow p-6 overflow-y-auto">
          {filteredMedia.length === 0 ? (
            <div className="text-center py-12">
              <Image className="mx-auto text-gray-400 mb-4" size={64} />
              <h3 className="text-xl font-semibold text-gray-700 dark:text-gray-300 mb-2">
                {t('media.noMediaFound')}
              </h3>
              <p className="text-gray-500 dark:text-gray-400">
                {searchQuery || selectedFolder || selectedTags.length > 0 
                  ? t('media.tryAdjustingFilters')
                  : t('media.uploadFirstMedia')}
              </p>
            </div>
          ) : viewMode === 'grid' ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 md:gap-4">
              {filteredMedia.map(item => (
                <div 
                  key={item.id}
                  className={`group relative bg-gray-50 dark:bg-gray-700 rounded-lg overflow-hidden hover:shadow-lg transition-all cursor-pointer border-2 ${
                    selectedMedia.includes(item.id)
                      ? 'border-primary-500'
                      : 'border-transparent'
                  }`}
                >
                  {/* Selection Checkbox */}
                  <div className="absolute top-2 left-2 z-10">
                    <input
                      type="checkbox"
                      checked={selectedMedia.includes(item.id)}
                      onChange={() => toggleMediaSelection(item.id)}
                      className="w-5 h-5 rounded cursor-pointer"
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>

                  {/* Preview */}
                  <div 
                    className="aspect-video bg-gradient-to-br from-gray-200 to-gray-300 dark:from-gray-600 dark:to-gray-700 flex items-center justify-center"
                    onClick={() => setShowPreviewModal(item)}
                  >
                    {item.type === 'image' && item.url ? (
                      <img 
                        src={`${API_BASE_URL}${item.thumbnail_url || item.url}`}
                        alt={item.name} 
                        className="w-full h-full object-cover"
                        loading="lazy"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.parentElement.innerHTML = `<div class="text-gray-400 dark:text-gray-500">${getMediaIcon(item.type)}</div>`;
                        }}
                      />
                    ) : item.type === 'webpage' ? (
                      <div className="flex flex-col items-center justify-center gap-2 h-full">
                        <Globe size={48} className="text-blue-500" />
                        <span className="text-xs text-gray-600 dark:text-gray-400">Website</span>
                      </div>
                    ) : item.type === 'pdf' ? (
                      <div className="flex flex-col items-center justify-center gap-2 h-full">
                        <FileText size={48} className="text-red-500" />
                        <span className="text-xs text-gray-600 dark:text-gray-400">PDF</span>
                      </div>
                    ) : (
                      <div className="text-gray-400 dark:text-gray-500">
                        {getMediaIcon(item.type)}
                      </div>
                    )}
                  </div>

                  {/* Info */}
                  <div className="p-3">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <h4 className="font-medium text-gray-900 dark:text-white truncate flex-1">
                        {item.name}
                      </h4>
                      <span className={`text-xs px-2 py-1 rounded ${getTypeBadgeColor(item.type)}`}>
                        {item.type.toUpperCase()}
                      </span>
                    </div>
                    
                    {item.duration && (
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {t('media.duration')}: {item.duration}s
                      </p>
                    )}
                  </div>

                  {/* Hover Actions */}
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-all flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowPreviewModal(item);
                      }}
                      className="bg-white text-gray-900 p-2 rounded-lg hover:bg-gray-100 transition-colors"
                      title={t('media.preview')}
                    >
                      <Eye size={20} />
                    </button>

                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowMediaTagModal(item);
                      }}
                      className="bg-purple-500 text-white p-2 rounded-lg hover:bg-purple-600 transition-colors"
                      title={t('media.manageTags')}
                    >
                      <Tag size={20} />
                    </button>

                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteMedia(item.id);
                      }}
                      className="bg-red-500 text-white p-2 rounded-lg hover:bg-red-600 transition-colors"
                      title={t('common.delete')}
                    >
                      <Trash2 size={20} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-2">
              {filteredMedia.map(item => (
                <div 
                  key={item.id}
                  className={`flex items-center gap-4 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors cursor-pointer border-2 ${
                    selectedMedia.includes(item.id)
                      ? 'border-primary-500'
                      : 'border-transparent'
                  }`}
                  onClick={() => setShowPreviewModal(item)}
                >
                  <input
                    type="checkbox"
                    checked={selectedMedia.includes(item.id)}
                    onChange={() => toggleMediaSelection(item.id)}
                    className="w-5 h-5 rounded cursor-pointer"
                    onClick={(e) => e.stopPropagation()}
                  />
                  <div className="text-gray-400">
                    {getMediaIcon(item.type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-gray-900 dark:text-white truncate">
                      {item.name}
                    </h4>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {item.type.toUpperCase()} {item.duration && `• ${item.duration}s`}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <button 
                      className="text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400"
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowPreviewModal(item);
                      }}
                    >
                      <Eye size={18} />
                    </button>
                    <button 
                      className="text-gray-600 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteMedia(item.id);
                      }}
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-2xl w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                {t('media.uploadMedia')}
              </h3>
              <button
                onClick={() => {
                  setShowUploadModal(false);
                  setSelectedFile(null);
                  setUploadMethod('file');
                }}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                <X size={24} />
              </button>
            </div>

            {/* TABS */}
            <div className="flex border-b border-gray-200 dark:border-gray-700 mb-6">
              <button
                onClick={() => setUploadMethod('file')}
                className={`flex-1 px-4 py-3 font-medium transition-colors ${
                  uploadMethod === 'file'
                    ? 'border-b-2 border-blue-600 text-blue-600 dark:text-blue-400'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                }`}
              >
                <div className="flex items-center justify-center gap-2">
                  <Upload size={20} />
                  <span>{t('media.fileUpload')}</span>
                </div>
              </button>

              <button
                onClick={() => setUploadMethod('url')}
                className={`flex-1 px-4 py-3 font-medium transition-colors ${
                  uploadMethod === 'url'
                    ? 'border-b-2 border-blue-600 text-blue-600 dark:text-blue-400'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                }`}
              >
                <div className="flex items-center justify-center gap-2">
                  <Globe size={20} />
                  <span>{t('media.urlWebsite')}</span>
                </div>
              </button>

              <button
                onClick={() => setUploadMethod('cloud')}
                className={`flex-1 px-4 py-3 font-medium transition-colors ${
                  uploadMethod === 'cloud'
                    ? 'border-b-2 border-blue-600 text-blue-600 dark:text-blue-400'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                }`}
              >
                <div className="flex items-center justify-center gap-2">
                  <Upload size={20} />
                  <span>{t('media.cloudStorage')}</span>
                </div>
              </button>
            </div>

            {/* TAB CONTENT */}
            <div className="space-y-4">
              {/* FILE UPLOAD TAB */}
              {uploadMethod === 'file' && (
                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                    {t('media.chooseFile')}
                  </label>
                  <input
  type="file"
  accept="image/*,video/*,.pdf,.mov"
  onChange={handleFileSelect}
  className="w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
/>
                  {selectedFile && (
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                      {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
                    </p>
                  )}
                </div>
              )}

              {/* URL TAB */}
              {uploadMethod === 'url' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                      {t('media.mediaName')} *
                    </label>
                    <input
                      type="text"
                      placeholder={t('media.mediaNamePlaceholder')}
                      className="w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                      id="urlMediaName"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                      {t('media.websiteUrl')} *
                    </label>
                    <input
                      type="url"
                      placeholder={t('media.websiteUrlPlaceholder')}
                      className="w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                      id="urlInput"
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {t('media.enterCompleteUrl')}
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                      {t('media.displayDuration')} *
                    </label>
                    <input
                      type="number"
                      defaultValue="30"
                      min="5"
                      className="w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                      id="urlDuration"
                    />
                  </div>
                </div>
              )}

              {/* CLOUD TAB */}
              {uploadMethod === 'cloud' && (
                <div className="text-center py-8">
                  <Upload className="mx-auto text-gray-400 mb-4" size={48} />
                  <h4 className="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    {t('media.cloudStorageIntegration')}
                  </h4>
                  <p className="text-gray-500 dark:text-gray-400 mb-4">
                    {t('media.connectCloudStorage')}
                  </p>
                  <p className="text-sm text-gray-400 dark:text-gray-500">
                    {t('media.comingSoon')}
                  </p>
                </div>
              )}

              {selectedFolder && (
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded p-3">
                  <p className="text-sm text-blue-900 dark:text-blue-100">
                    {t('media.uploadingTo')}: <strong>{folders.find(f => f.id === selectedFolder)?.name}</strong>
                  </p>
                </div>
              )}
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={uploadMedia}
                disabled={uploading || (uploadMethod === 'file' && !selectedFile)}
                className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white px-4 py-2 rounded transition-colors"
              >
                {uploading ? t('media.uploading') : uploadMethod === 'url' ? t('media.addWebsite') : t('media.upload')}
              </button>
              <button
                onClick={() => {
                  setShowUploadModal(false);
                  setSelectedFile(null);
                  setUploadMethod('file');
                }}
                className="flex-1 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200 px-4 py-2 rounded transition-colors"
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {showPreviewModal && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                  {showPreviewModal.name}
                </h3>
                <button
                  onClick={() => setShowPreviewModal(null)}
                  className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                >
                  <X size={24} />
                </button>
              </div>

              {/* Preview Content */}
              <div className="mb-4">
                {showPreviewModal.type === 'image' && (
                  <img 
                    src={`${API_BASE_URL}${showPreviewModal.url}`} 
                    alt={showPreviewModal.name} 
                    className="w-full rounded-lg"
                    onError={(e) => {
                      console.error('Image failed to load:', showPreviewModal.url);
                      e.target.src = 'https://via.placeholder.com/400x300?text=Image+Not+Found';
                    }}
                  />
                )}
                {showPreviewModal.type === 'video' && (
                  <video 
                    src={`${API_BASE_URL}${showPreviewModal.url}`} 
                    controls 
                    className="w-full rounded-lg"
                  />
                )}
                {showPreviewModal.type === 'webpage' && (
                  <div className="aspect-video bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center">
                    <a 
                      href={showPreviewModal.url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-primary-600 hover:text-blue-700 dark:text-primary-400"
                    >
                      {t('media.openUrl')} {showPreviewModal.url}
                    </a>
                  </div>
                )}
              </div>

              {/* Details */}
              <div className="space-y-2 text-sm">
                <p className="text-gray-700 dark:text-gray-300">
                  <span className="font-semibold">{t('media.type')}:</span> {showPreviewModal.type.toUpperCase()}
                </p>
                {showPreviewModal.duration && (
                  <p className="text-gray-700 dark:text-gray-300">
                    <span className="font-semibold">{t('media.duration')}:</span> {showPreviewModal.duration}s
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Folder Modal */}
      {showFolderModal && <FolderModal />}

      {/* Tag Manager Modal */}
      {showTagModal && (
        <TagManagerModal 
          tags={tags}
          onClose={() => setShowTagModal(false)}
          onCreateTag={handleCreateTag}
          onDeleteTag={deleteTag}
        />
      )}

      {/* Media Tag Assignment Modal */}
      {showMediaTagModal && <MediaTagModal media={showMediaTagModal} />}

    </div>
  );
};

export default EnhancedMediaLibrary;